// Ghidha — Immersive landscape view.
// Implements the "Ghidha Immersive" design: ambient bloom + grid + drifting
// embers behind a glass cockpit with a hero energy ring, two semi-arc side
// stats (BURNED · REMAINING), three macro dials, and compact activity / sleep
// tiles. Scaled down from the desktop mock to fit a phone in landscape.

import { useContext, useEffect, useState } from "react";
import { VitalsCtx } from "./CalorieCounter";
import {
  IMM as C, IMM_MONO as MONO, IMM_SERIF as SERIF,
  Ambient, Embers, CardCorners, MicroHd, useImmAnim as useAnim,
  IMM_KEYFRAMES,
} from "./immersive";

// ─── helpers ───────────────────────────────────────────────────────
const polarPt = (cx, cy, r, deg) => {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
};
const arcPath = (cx, cy, r, startDeg, endDeg) => {
  const s = polarPt(cx, cy, r, startDeg);
  const e = polarPt(cx, cy, r, endDeg);
  const large = Math.abs(endDeg - startDeg) <= 180 ? 0 : 1;
  const sweep = endDeg >= startDeg ? 1 : 0;
  return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} ${sweep} ${e.x} ${e.y}`;
};
function useTick() {
  const [t, setT] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const step = () => {
      setT((performance.now() - start) / 1000);
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);
  return t;
}

// Hero ring — large, layered, animated
function HeroRing({ intake, goal, burned, accent, size, onTap }) {
  const pct = Math.min(1.2, intake / Math.max(1, goal));
  const animPct = useAnim(pct, 1500, 100);
  const animIntake = useAnim(intake, 1500, 100);
  const burnPct = Math.min(1, burned / 3000);
  const animBurnPct = useAnim(burnPct, 1400, 200);
  const t = useTick();

  const stroke = Math.max(10, Math.round(size * 0.045));
  const r = (size - stroke) / 2 - 14;
  const cx = size / 2, cy = size / 2;
  const Cc = 2 * Math.PI * r;
  const remaining = goal - intake;

  // tick marks
  const ticks = [];
  for (let i = 0; i < 64; i++) {
    const angle = (i / 64) * 360 - 90;
    const major = i % 8 === 0;
    const inner = polarPt(cx, cy, r + stroke / 2 + 3, angle);
    const outer = polarPt(cx, cy, r + stroke / 2 + (major ? 8 : 3), angle);
    ticks.push(
      <line key={i} x1={inner.x} y1={inner.y} x2={outer.x} y2={outer.y}
        stroke={major ? "rgba(224,160,48,0.55)" : "rgba(224,160,48,0.15)"}
        strokeWidth={major ? 1 : 0.5}/>
    );
  }
  const labels = [
    { deg: -90, text: "0" },
    { deg: 0, text: Math.round(goal / 4).toString() },
    { deg: 90, text: Math.round(goal / 2).toString() },
    { deg: 180, text: Math.round((goal * 3) / 4).toString() },
  ];
  const angle = -90 + animPct * 360;
  const dot = polarPt(cx, cy, r, angle);
  const sweepAngle = -90 + ((t * 22) % 360);
  const sweepEnd = polarPt(cx, cy, r + stroke, sweepAngle);

  const burnR = r + stroke + 10;

  return (
    <div onClick={onTap}
      role={onTap ? "button" : undefined}
      aria-label={onTap ? "Show today's meals" : undefined}
      style={{
        position: "relative", width: size, height: size,
        cursor: onTap ? "pointer" : "default",
        touchAction: "manipulation", WebkitTapHighlightColor: "transparent",
      }}>
      <svg width={size} height={size} style={{ overflow: "visible", display: "block" }}>
        <defs>
          <linearGradient id="ghidhaAmberGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={C.amberSoft}/>
            <stop offset="100%" stopColor={accent || C.amber}/>
          </linearGradient>
          <radialGradient id="ghidhaCenterGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={accent || C.amber} stopOpacity="0.22"/>
            <stop offset="60%" stopColor={accent || C.amber} stopOpacity="0.05"/>
            <stop offset="100%" stopColor={accent || C.amber} stopOpacity="0"/>
          </radialGradient>
        </defs>
        <circle cx={cx} cy={cy} r={r - 6} fill="url(#ghidhaCenterGlow)"/>
        <g>{ticks}</g>
        {/* burn counter-arc */}
        <circle cx={cx} cy={cy} r={burnR} stroke="rgba(124,196,160,0.10)" strokeWidth="1.5" fill="none"/>
        <circle cx={cx} cy={cy} r={burnR}
          stroke={C.sage} strokeWidth="1.5" fill="none"
          strokeDasharray={`${2 * Math.PI * burnR * animBurnPct} ${2 * Math.PI * burnR}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cy})`}
          style={{ filter: `drop-shadow(0 0 4px ${C.sage}88)` }}/>
        {/* track */}
        <circle cx={cx} cy={cy} r={r} stroke="rgba(255,255,255,0.05)" strokeWidth={stroke} fill="none"/>
        {/* fill */}
        <circle cx={cx} cy={cy} r={r}
          stroke="url(#ghidhaAmberGrad)" strokeWidth={stroke} fill="none"
          strokeDasharray={`${Cc * Math.min(animPct, 1)} ${Cc}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cy})`}
          style={{ animation: "immBreath 4.5s ease-in-out infinite" }}/>
        {animPct > 0.01 && (
          <>
            <circle cx={dot.x} cy={dot.y} r={7} fill={accent || C.amber} opacity="0.25"/>
            <circle cx={dot.x} cy={dot.y} r={4} fill={accent || C.amber}
              style={{ filter: `drop-shadow(0 0 8px ${accent || C.amber})` }}/>
            <circle cx={dot.x} cy={dot.y} r={1.6} fill="#fff"/>
          </>
        )}
        <line x1={cx} y1={cy} x2={sweepEnd.x} y2={sweepEnd.y}
          stroke={accent || C.amber} strokeWidth="0.8" opacity="0.18"/>
        {labels.map((l) => {
          const p = polarPt(cx, cy, r + stroke + 18, l.deg);
          return (
            <text key={l.text} x={p.x} y={p.y}
              fontFamily={MONO} fontSize="8" letterSpacing="0.1em"
              fill={C.ink4} textAnchor="middle" dominantBaseline="middle">{l.text}</text>
          );
        })}
      </svg>
      <div style={{
        position: "absolute", inset: 0,
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        pointerEvents: "none",
      }}>
        <div style={{ fontFamily: MONO, fontSize: 8.5, color: C.ink3, letterSpacing: "0.22em", marginBottom: 4 }}>
          INTAKE · KCAL
        </div>
        <div style={{
          fontFamily: SERIF, fontSize: Math.round(size * 0.22),
          fontWeight: 300, color: C.ink1, letterSpacing: "-0.04em", lineHeight: 0.95,
          textShadow: `0 0 24px ${(accent || C.amber)}55`, fontVariantNumeric: "tabular-nums",
        }}>
          {Math.round(animIntake).toLocaleString()}
        </div>
        <div style={{ fontFamily: MONO, fontSize: 9.5, color: C.ink3, marginTop: 6, letterSpacing: "0.14em" }}>
          {Math.round(animPct * 100)}<span style={{ color: C.ink4 }}>% / </span>{(goal || 0).toLocaleString()}
        </div>
        <div style={{
          display: "flex", alignItems: "center", gap: 6,
          marginTop: 8, padding: "3px 8px",
          borderRadius: 999,
          border: "0.5px solid rgba(224,160,48,0.35)",
          background: "rgba(224,160,48,0.07)",
        }}>
          <span style={{
            width: 5, height: 5, borderRadius: "50%",
            background: C.amber, boxShadow: `0 0 8px ${C.amber}`,
            animation: "immLiveBlink 2s ease-in-out infinite",
          }}/>
          <span style={{ fontFamily: MONO, fontSize: 8.5, color: C.amberSoft, letterSpacing: "0.18em" }}>
            {remaining >= 0 ? `${Math.round(remaining).toLocaleString()} LEFT` : `+${Math.abs(Math.round(remaining)).toLocaleString()} OVER`}
          </span>
        </div>
      </div>
    </div>
  );
}

// Side semi-arc stat (BURNED / REMAINING)
function SideStat({ label, val, sub, pct, color, align = "left", delay = 0, size = 84, onTap }) {
  const animPct = useAnim(Math.min(1, pct), 1300, delay);
  const animVal = useAnim(parseFloat(String(val).replace(/[, ]/g, "")) || 0, 1300, delay);
  const stroke = 6;
  const r = (size - stroke) / 2 - 4;
  const cx = size / 2, cy = size / 2;
  const startDeg = align === "left" ? 90 : -90;
  const endDeg = align === "left" ? 270 : 90;
  const totalDeg = 180;
  const trackPath = arcPath(cx, cy, r, startDeg, endDeg);
  const fillEnd = startDeg + totalDeg * animPct;
  const fillPath = animPct > 0.005 ? arcPath(cx, cy, r, startDeg, fillEnd) : null;
  const dot = polarPt(cx, cy, r, fillEnd);
  const ticks = [];
  for (let i = 0; i <= 12; i++) {
    const a = startDeg + (i / 12) * totalDeg;
    const inner = polarPt(cx, cy, r + stroke / 2 + 2, a);
    const outer = polarPt(cx, cy, r + stroke / 2 + (i % 4 === 0 ? 6 : 3), a);
    ticks.push(
      <line key={i} x1={inner.x} y1={inner.y} x2={outer.x} y2={outer.y}
        stroke={i % 4 === 0 ? color : C.line} strokeOpacity={i % 4 === 0 ? 0.55 : 1}
        strokeWidth={i % 4 === 0 ? 0.9 : 0.5}/>
    );
  }
  return (
    <div onClick={onTap}
      role={onTap ? "button" : undefined}
      tabIndex={onTap ? 0 : undefined}
      onKeyDown={onTap ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onTap(); } } : undefined}
      style={{
        display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
        cursor: onTap ? "pointer" : "default",
        touchAction: "manipulation", WebkitTapHighlightColor: "transparent",
      }}>
      <div style={{ fontFamily: MONO, fontSize: 9, color, letterSpacing: "0.22em", fontWeight: 600 }}>{label}</div>
      <div style={{ position: "relative", width: size, height: size }}>
        <svg width={size} height={size} style={{ overflow: "visible", display: "block" }}>
          {ticks}
          <path d={trackPath} stroke="rgba(255,255,255,0.05)" strokeWidth={stroke} fill="none" strokeLinecap="round"/>
          {fillPath && (
            <path d={fillPath} stroke={color} strokeWidth={stroke} fill="none" strokeLinecap="round"
              style={{ filter: `drop-shadow(0 0 6px ${color})` }}/>
          )}
          {animPct > 0.01 && (
            <>
              <circle cx={dot.x} cy={dot.y} r={5} fill={color} opacity="0.25"/>
              <circle cx={dot.x} cy={dot.y} r={2.8} fill={color}
                style={{ filter: `drop-shadow(0 0 5px ${color})` }}/>
            </>
          )}
        </svg>
        <div style={{
          position: "absolute", inset: 0,
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
        }}>
          <div style={{
            fontFamily: SERIF, fontSize: Math.round(size * 0.22),
            fontWeight: 300, color: C.ink1, letterSpacing: "-0.03em", lineHeight: 0.95,
            fontVariantNumeric: "tabular-nums",
          }}>
            {Math.round(animVal).toLocaleString()}
          </div>
          <div style={{ fontFamily: MONO, fontSize: 7.5, color: C.ink4, marginTop: 2, letterSpacing: "0.16em" }}>KCAL</div>
        </div>
      </div>
      <div style={{ fontFamily: MONO, fontSize: 8, color: C.ink3, letterSpacing: "0.16em", marginTop: 1 }}>{sub}</div>
    </div>
  );
}

// Macro dial (270° arc + ticks + dot + sparkline)
function MacroDial({ k, val, target, color, delay, onTap }) {
  const targetPct = Math.min(1.2, val / Math.max(1, target));
  const animPct = useAnim(targetPct, 1100, delay);
  const animVal = useAnim(val, 1100, delay);
  const over = val > target;
  const remaining = target - val;
  const size = 92;
  const stroke = 7;
  const r = (size - stroke) / 2 - 4;
  const cx = size / 2, cy = size / 2;
  const startDeg = 135, totalDeg = 270;
  const trackPath = arcPath(cx, cy, r, startDeg, startDeg + totalDeg);
  const fillEnd = startDeg + totalDeg * Math.min(1, animPct);
  const fillPath = animPct > 0.005 ? arcPath(cx, cy, r, startDeg, fillEnd) : null;
  const overEnd = over ? startDeg + totalDeg * Math.min(1, animPct - 1) : null;
  const dot = polarPt(cx, cy, r, fillEnd);
  return (
    <div onClick={onTap}
      role={onTap ? "button" : undefined}
      aria-label={onTap ? `${k} breakdown` : undefined}
      style={{
        position: "relative", flex: 1, minWidth: 0,
        padding: "10px 8px 9px",
        background: "linear-gradient(180deg, rgba(20,20,26,0.55), rgba(10,10,14,0.35))",
        border: "0.5px solid rgba(255,255,255,0.06)",
        borderRadius: 14,
        backdropFilter: "blur(10px) saturate(130%)",
        WebkitBackdropFilter: "blur(10px) saturate(130%)",
        display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
        cursor: onTap ? "pointer" : "default",
        touchAction: "manipulation", WebkitTapHighlightColor: "transparent",
        overflow: "hidden",
      }}>
      <CardCorners/>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", position: "relative", zIndex: 1 }}>
        <div style={{ fontFamily: MONO, fontSize: 8.5, color, letterSpacing: "0.2em", fontWeight: 700 }}>
          {k.toUpperCase()}
        </div>
        <div style={{
          fontFamily: MONO, fontSize: 7.5,
          color: over ? C.coralSoft : C.ink3,
          letterSpacing: "0.1em",
          padding: "1px 5px", borderRadius: 999,
          border: `0.5px solid ${over ? "rgba(217,122,106,0.35)" : "rgba(255,255,255,0.06)"}`,
          background: over ? "rgba(217,122,106,0.08)" : "rgba(255,255,255,0.02)",
        }}>
          {over ? `+${Math.round(val - target)}g` : `${Math.round(remaining)}g LEFT`}
        </div>
      </div>
      <div style={{ position: "relative", width: size, height: size, marginTop: 2 }}>
        <svg width={size} height={size} style={{ overflow: "visible", display: "block" }}>
          <path d={trackPath} stroke="rgba(255,255,255,0.05)" strokeWidth={stroke} fill="none" strokeLinecap="round"/>
          {fillPath && (
            <path d={fillPath} stroke={color} strokeWidth={stroke} fill="none" strokeLinecap="round"
              style={{ filter: `drop-shadow(0 0 6px ${color})` }}/>
          )}
          {over && overEnd && (
            <path d={arcPath(cx, cy, r, startDeg, overEnd)}
              stroke="#C24A3A" strokeWidth={stroke} fill="none" strokeLinecap="round"
              style={{ filter: "drop-shadow(0 0 5px #C24A3A)", mixBlendMode: "screen" }}/>
          )}
          {animPct > 0.01 && (
            <>
              <circle cx={dot.x} cy={dot.y} r={5} fill={color} opacity="0.25"/>
              <circle cx={dot.x} cy={dot.y} r={2.8} fill={color}
                style={{ filter: `drop-shadow(0 0 5px ${color})` }}/>
            </>
          )}
        </svg>
        <div style={{
          position: "absolute", inset: 0,
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
        }}>
          <div style={{
            fontFamily: SERIF, fontSize: 22, fontWeight: 300,
            color: C.ink1, letterSpacing: "-0.03em", lineHeight: 0.95,
            fontVariantNumeric: "tabular-nums",
          }}>
            {Math.round(animVal)}
          </div>
          <div style={{ fontFamily: MONO, fontSize: 7.5, color: C.ink4, marginTop: 2, letterSpacing: "0.12em" }}>
            / {target}g
          </div>
        </div>
      </div>
    </div>
  );
}

function ActivityTile({ steps, stepsGoal, burnedActive, onTap }) {
  const pct = Math.min(1, steps / Math.max(1, stepsGoal));
  const animPct = useAnim(pct, 1300, 350);
  const animSteps = useAnim(steps, 1300, 350);
  const animKcal = useAnim(burnedActive, 1300, 400);
  const hours = [0.2, 0.4, 0.3, 0.6, 0.8, 1, 0.7, 0.5, 0.6, 0.4, 0.7, 0.9];
  return (
    <div onClick={onTap}
      role={onTap ? "button" : undefined}
      style={{
        position: "relative", flex: 1, minWidth: 0, minHeight: 0,
        padding: "10px 12px",
        background: "linear-gradient(180deg, rgba(20,20,26,0.55), rgba(10,10,14,0.35))",
        border: "0.5px solid rgba(255,255,255,0.06)",
        borderRadius: 14,
        cursor: onTap ? "pointer" : "default",
        touchAction: "manipulation", WebkitTapHighlightColor: "transparent",
        display: "flex", flexDirection: "column", overflow: "hidden",
      }}>
      <CardCorners/>
      <MicroHd num="03" label="ACTIVITY" right="LIVE"/>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8, flex: 1, minHeight: 0 }}>
        <div style={{ position: "relative", width: 64, height: 64, flexShrink: 0 }}>
          <svg width="64" height="64">
            <circle cx="32" cy="32" r="26" stroke="rgba(255,255,255,0.05)" strokeWidth="4" fill="none"/>
            <circle cx="32" cy="32" r="26"
              stroke={C.amber} strokeWidth="4" fill="none"
              strokeDasharray={`${2 * Math.PI * 26 * animPct} ${2 * Math.PI * 26}`}
              strokeLinecap="round"
              transform="rotate(-90 32 32)"
              style={{ filter: `drop-shadow(0 0 5px ${C.amber})` }}/>
          </svg>
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <div style={{ fontFamily: SERIF, fontSize: 16, color: C.ink1, fontWeight: 300, letterSpacing: "-0.02em", lineHeight: 0.95 }}>
              {(Math.round(animSteps / 100) / 10).toFixed(1)}<span style={{ fontSize: 9, color: C.ink3 }}>k</span>
            </div>
            <div style={{ fontFamily: MONO, fontSize: 7, color: C.ink4, marginTop: 1, letterSpacing: "0.16em" }}>STEPS</div>
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: MONO, fontSize: 8, color: C.ink3, letterSpacing: "0.18em", marginBottom: 4 }}>
            ACTIVE · LAST 12H
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 26 }}>
            {hours.map((h, i) => (
              <div key={i} style={{
                flex: 1, height: `${h * 26}px`, borderRadius: 1,
                background: "linear-gradient(180deg, #F0C26B, #A87420)",
                opacity: 0.4 + h * 0.5,
              }}/>
            ))}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 12, marginTop: 6, paddingTop: 6, borderTop: `0.5px solid ${C.lineSoft}` }}>
        <div>
          <div style={{ fontFamily: MONO, fontSize: 7.5, color: C.ink4, letterSpacing: "0.16em" }}>BURNED</div>
          <div style={{ fontFamily: SERIF, fontSize: 14, color: C.ink1, fontWeight: 400, fontVariantNumeric: "tabular-nums" }}>
            {Math.round(animKcal).toLocaleString()} <span style={{ fontFamily: MONO, fontSize: 7.5, color: C.ink3 }}>KCAL</span>
          </div>
        </div>
        <div>
          <div style={{ fontFamily: MONO, fontSize: 7.5, color: C.ink4, letterSpacing: "0.16em" }}>OF GOAL</div>
          <div style={{ fontFamily: SERIF, fontSize: 14, color: C.ink1, fontWeight: 400, fontVariantNumeric: "tabular-nums" }}>
            {Math.round(animPct * 100)}<span style={{ fontFamily: MONO, fontSize: 7.5, color: C.ink3 }}>%</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function SleepTile({ totalMins, deepMins, remMins, awakeMins, onTap }) {
  const lightMins = Math.max(0, totalMins - deepMins - remMins - awakeMins);
  const totalH = Math.floor(totalMins / 60);
  const totalM = totalMins % 60;
  const goal = 480;
  const animQuality = useAnim(Math.min(100, Math.round((totalMins / goal) * 100)), 1300, 450);
  const stages = [
    { k: "AWAKE", col: "rgba(244,239,224,0.18)", mins: awakeMins },
    { k: "REM",   col: C.coralSoft,              mins: remMins },
    { k: "LIGHT", col: C.cream,                  mins: lightMins },
    { k: "DEEP",  col: C.amber,                  mins: deepMins },
  ];
  return (
    <div onClick={onTap}
      role={onTap ? "button" : undefined}
      style={{
        position: "relative", flex: 1, minWidth: 0, minHeight: 0,
        padding: "10px 12px",
        background: "linear-gradient(180deg, rgba(20,20,26,0.55), rgba(10,10,14,0.35))",
        border: "0.5px solid rgba(255,255,255,0.06)",
        borderRadius: 14,
        cursor: onTap ? "pointer" : "default",
        touchAction: "manipulation", WebkitTapHighlightColor: "transparent",
        display: "flex", flexDirection: "column", overflow: "hidden",
      }}>
      <CardCorners/>
      <MicroHd num="04" label="SLEEP"/>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginTop: 8, flex: 1, minHeight: 0 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: SERIF, fontSize: 22, fontWeight: 300, color: C.ink1, letterSpacing: "-0.03em", lineHeight: 0.95, fontVariantNumeric: "tabular-nums" }}>
            {totalH}<span style={{ fontSize: 11, color: C.ink3 }}>h</span>{String(totalM).padStart(2, "0")}<span style={{ fontSize: 11, color: C.ink3 }}>m</span>
          </div>
          <div style={{ fontFamily: MONO, fontSize: 7.5, color: C.ink4, letterSpacing: "0.18em", marginTop: 3 }}>
            SLEPT · GOAL 8H
          </div>
          <div style={{ marginTop: 7, display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ fontFamily: MONO, fontSize: 7.5, color: C.ink3, letterSpacing: "0.16em" }}>QUALITY</div>
            <div style={{ fontFamily: SERIF, fontSize: 13, color: C.amber, fontWeight: 400 }}>{Math.round(animQuality)}</div>
            <div style={{ width: 38, height: 2.5, background: "rgba(255,255,255,0.06)", borderRadius: 2, position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", inset: 0, width: `${animQuality}%`, background: `linear-gradient(90deg, ${C.amberDeep}, ${C.amberSoft})`, borderRadius: 2 }}/>
            </div>
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}/>
      </div>
      <div style={{ display: "flex", gap: 5, marginTop: 6, paddingTop: 6, borderTop: `0.5px solid ${C.lineSoft}` }}>
        {stages.map((s) => (
          <div key={s.k} style={{ flex: 1, minWidth: 0 }}>
            <div style={{ height: 2.5, background: s.col, marginBottom: 3, opacity: 0.7 }}/>
            <div style={{ fontFamily: MONO, fontSize: 7, color: C.ink4, letterSpacing: "0.12em", marginBottom: 1 }}>{s.k}</div>
            <div style={{ fontFamily: MONO, fontSize: 9, color: C.ink1, fontVariantNumeric: "tabular-nums" }}>
              {Math.floor(s.mins / 60)}h {String(s.mins % 60).padStart(2, "0")}m
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────
export default function GhidhaImmersive({ onOpenMetric, onRingTap }) {
  const ctx = useContext(VitalsCtx);
  if (!ctx) return null;
  const { totalCalories, totalBurned, totalProtein, totalCarbs, totalFat, goals, todayData } = ctx;

  const intake = Math.round(totalCalories || 0);
  const goal = goals.calories || 2000;
  const burned = Math.round(totalBurned || 0);
  const remaining = goal - intake;

  const steps = todayData?.steps || 0;
  const stepsGoal = goals.steps || 12000;
  const burnedActive = todayData?.caloriesBurned || 0;
  const sleepMins = todayData?.sleepMinutes || 0;
  const deepMins = todayData?.deepMinutes || 0;
  const remMins = todayData?.remMinutes || 0;
  const awakeMins = todayData?.awakeMinutes || 0;

  return (
    <div style={{
      position: "relative",
      width: "100%", height: "100%",
      flex: 1, minHeight: 0,
      borderRadius: 20,
      overflow: "hidden",
      background: `linear-gradient(180deg, ${C.bg0} 0%, #08080c 100%)`,
      border: "0.5px solid rgba(255,255,255,0.05)",
      boxShadow: "0 10px 40px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.04)",
      color: C.ink1,
      display: "flex", flexDirection: "column",
    }}>
      <style>{IMM_KEYFRAMES}</style>
      <Ambient/>
      <Embers count={14}/>

      {/* Eyebrow row */}
      <div style={{ position: "relative", zIndex: 2, padding: "10px 16px 4px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontFamily: MONO, fontSize: 9.5, color: C.amber, letterSpacing: "0.22em", fontWeight: 600 }}>01</span>
          <span style={{ flex: 1, height: 0.5, background: `linear-gradient(90deg, ${C.amber}, transparent)` }}/>
          <span style={{ fontFamily: MONO, fontSize: 9.5, color: C.ink2, letterSpacing: "0.22em" }}>ENERGY BALANCE</span>
          <span style={{ flex: 1, height: 0.5, background: `linear-gradient(90deg, transparent, ${C.line})` }}/>
          <span style={{ fontFamily: MONO, fontSize: 8.5, color: C.ink4, letterSpacing: "0.16em" }}>GHIDHA · TODAY</span>
        </div>
        <div style={{
          fontFamily: '"Instrument Serif",Georgia,serif', fontStyle: "italic",
          fontSize: 12, color: C.ink3, textAlign: "center", marginTop: 4,
        }}>
          <span style={{ color: C.amberSoft }}>Today's intent</span> &nbsp;·&nbsp; eat with restraint, leave a third for breath
        </div>
      </div>

      {/* Main grid */}
      <div style={{
        position: "relative", zIndex: 2,
        display: "grid",
        gridTemplateColumns: "1.2fr 1fr",
        gap: 10,
        padding: "4px 12px 8px",
        flex: 1, minHeight: 0,
      }}>
        {/* LEFT: Energy Balance hero card */}
        <div style={{
          position: "relative",
          padding: "10px 14px",
          background: "linear-gradient(180deg, rgba(20,20,26,0.55), rgba(10,10,14,0.35))",
          border: "0.5px solid rgba(255,255,255,0.06)",
          borderRadius: 16,
          backdropFilter: "blur(12px) saturate(140%)",
          WebkitBackdropFilter: "blur(12px) saturate(140%)",
          overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}>
          <CardCorners/>
          <div style={{
            position: "absolute", inset: 0,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.014) 1px, transparent 1px)," +
              "linear-gradient(90deg, rgba(255,255,255,0.014) 1px, transparent 1px)",
            backgroundSize: "28px 28px",
            WebkitMaskImage: "radial-gradient(ellipse 80% 80% at 50% 50%, #000 30%, transparent 90%)",
            maskImage: "radial-gradient(ellipse 80% 80% at 50% 50%, #000 30%, transparent 90%)",
            pointerEvents: "none",
          }}/>
          <div style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr auto",
            alignItems: "center", gap: 8,
            flex: 1, minHeight: 0,
            position: "relative", zIndex: 1,
          }}>
            <SideStat
              label="BURNED" val={burned} sub="active"
              pct={burned / 3000} color={C.sage} align="left" delay={250}
              size={84}
              onTap={onOpenMetric ? () => onOpenMetric("burned") : undefined}/>
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minWidth: 0 }}>
              <HeroRing
                intake={intake} goal={goal} burned={burned}
                accent={C.amber}
                size={Math.min(220, Math.max(170, Math.round(window.innerHeight * 0.62)))}
                onTap={onRingTap}/>
            </div>
            <SideStat
              label={remaining >= 0 ? "REMAINING" : "OVER"}
              val={Math.abs(remaining)}
              sub={remaining >= 0 ? "budget" : "over budget"}
              pct={Math.abs(remaining) / Math.max(1, goal)}
              color={remaining < 0 ? C.coralSoft : C.amber}
              align="right" delay={350}
              size={84}
              onTap={onOpenMetric ? () => onOpenMetric("remaining") : undefined}/>
          </div>
        </div>

        {/* RIGHT: macros + activity/sleep stack */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8, minHeight: 0, minWidth: 0 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <MicroHd num="02" label="MACROS · GRAMS" right="TAP TO EXPAND"/>
            <div style={{ display: "flex", gap: 6 }}>
              <MacroDial k="Protein" val={Math.round(totalProtein || 0)} target={goals.protein || 150} color={C.coralSoft} delay={100} onTap={onOpenMetric ? () => onOpenMetric("protein") : undefined}/>
              <MacroDial k="Carbs"   val={Math.round(totalCarbs || 0)}   target={goals.carbs || 225}   color={C.amber}     delay={180} onTap={onOpenMetric ? () => onOpenMetric("carbs")   : undefined}/>
              <MacroDial k="Fat"     val={Math.round(totalFat || 0)}     target={goals.fat || 65}      color={C.coralSoft} delay={260} onTap={onOpenMetric ? () => onOpenMetric("fat")     : undefined}/>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, flex: 1, minHeight: 0 }}>
            <ActivityTile steps={steps} stepsGoal={stepsGoal} burnedActive={burnedActive}
              onTap={onOpenMetric ? () => onOpenMetric("activity") : undefined}/>
            <SleepTile totalMins={sleepMins} deepMins={deepMins} remMins={remMins} awakeMins={awakeMins}
              onTap={onOpenMetric ? () => onOpenMetric("sleep") : undefined}/>
          </div>
        </div>
      </div>
    </div>
  );
}
